import { supabase } from '@/integrations/supabase/client';
import { Charge } from '@/components/company/types/planTypes';

// Type assertion to allow access to tables not defined in the database types
const supabaseClient = supabase as any;

/**
 * Updates the verification status of a screenshot in the scrapes table
 * 
 * @param slug The company slug
 * @param version The version of the scrape
 * @param verified The new verification status (true = valid, false = invalid)
 * @returns Object with success status and message
 */
export async function updateScreenshotVerification(
  slug: string,
  version: string,
  verified: boolean
): Promise<{ success: boolean; message: string; error?: string }> {
  if (!slug || !version) {
    return {
      success: false,
      message: 'Company slug and version are required',
      error: 'Missing required parameters'
    };
  }

  try {
    console.log(`🔄 [chargeUtils] Updating screenshot verification for ${slug} version ${version} to ${verified ? 'valid' : 'invalid'}`);
    
    // Update the verified field in the scrapes table
    const { error: updateError } = await supabaseClient
      .from('scrapes')
      .update({ verified })
      .eq('slug', slug)
      .eq('version', version);
    
    if (updateError) {
      console.error('❌ [chargeUtils] Error updating screenshot verification:', updateError);
      return {
        success: false,
        message: 'Failed to update screenshot verification status',
        error: updateError.message
      };
    }
    
    console.log(`✅ [chargeUtils] Successfully updated screenshot verification for ${slug} version ${version}`);
    
    return {
      success: true,
      message: `Successfully marked screenshot as ${verified ? 'valid' : 'invalid'}`
    };
  } catch (error) {
    console.error('❌ [chargeUtils] Unexpected error in updateScreenshotVerification:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}

// The toggleChargeInVersion function has been migrated to GraphQL.
// For charge mapping operations, please use toggleChargeMappingMutation from /src/graphql/mutations/toggleChargeMapping.ts

/**
 * Updates an existing charge with new values
 * 
 * @param chargeId The ID of the charge to update
 * @param updateData The updated charge data (price, currency, price_per, per, type)
 * @returns Object with success status and message
 */
export async function updateCharge(
  chargeId: string,
  updateData: {
    price: number | null;
    currency: string | null;
    price_per?: string | null;
    per?: string | null;
    type?: string | null;
    pricing_metric_id?: string | null;
  }
): Promise<{ success: boolean; message: string; updatedCharge?: Charge; error?: string }> {
  if (!chargeId) {
    return {
      success: false,
      message: 'Charge ID is required',
      error: 'Missing required parameter: chargeId'
    };
  }

  try {
    console.log(`🔄 [chargeUtils] Updating charge ${chargeId} with data:`, updateData);
    
    // Create update object
    const updateObject: any = {
      price: updateData.price,
      currency: updateData.currency,
      price_per: updateData.price_per || updateData.per,
    };
    
    // Add type to update if provided
    if (updateData.type !== undefined) {
      updateObject.type = updateData.type;
    }
    
    // Add pricing_metric_id to update if provided (can be null to remove the association)
    if (updateData.pricing_metric_id !== undefined) {
      updateObject.pricing_metric_id = updateData.pricing_metric_id;
      console.log(`🔄 [chargeUtils] ${updateData.pricing_metric_id ? 'Setting' : 'Removing'} metric association for charge ${chargeId}${updateData.pricing_metric_id ? ` to ${updateData.pricing_metric_id}` : ''}`);
    }
    
    // Update charge in the database
    const { data: updatedCharge, error: updateError } = await supabaseClient
      .from('charges')
      .update(updateObject)
      .eq('id', chargeId)
      .select('*')
      .single();
    
    if (updateError) {
      console.error('❌ [chargeUtils] Error updating charge:', updateError);
      return {
        success: false,
        message: 'Failed to update charge',
        error: updateError.message
      };
    }
    
    console.log(`✅ [chargeUtils] Successfully updated charge ${chargeId}`, updatedCharge);
    
    return {
      success: true,
      message: `Successfully updated charge`,
      updatedCharge: updatedCharge as Charge
    };
  } catch (error) {
    console.error('❌ [chargeUtils] Unexpected error in updateCharge:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}

// The createCharge function has been migrated to GraphQL.
// For charge creation, please use the addChargeMutation from /src/graphql/mutations/addCharge.ts

export async function removeCharge(
  chargeId: string,
  slug: string
): Promise<{ success: boolean; message: string; error?: string }> {
  if (!chargeId || !slug) {
    return {
      success: false,
      message: 'Charge ID and company slug are required',
      error: 'Missing required parameters'
    };
  }

  try {
    console.log(`🔄 [chargeUtils] Deleting charge ${chargeId} for slug ${slug}`);
    
    // First, check for existing entries in charges_map
    const { data: existingMappings, error: checkError } = await supabaseClient
      .from('charges_map')
      .select('id, charge_id, slug, version')
      .eq('charge_id', chargeId);
    
    if (checkError) {
      console.error('❌ [chargeUtils] Error checking charges_map:', checkError);
      return {
        success: false,
        message: 'Failed to check charge mappings',
        error: checkError.message
      };
    }
    
    console.log(`ℹ️ [chargeUtils] Found ${existingMappings?.length || 0} mappings for charge ${chargeId}`);
    
    // Step 1: Delete entries from charges_map
    const { error: mapDeleteError } = await supabaseClient
      .from('charges_map')
      .delete()
      .eq('charge_id', chargeId);
    
    if (mapDeleteError) {
      console.error('❌ [chargeUtils] Error deleting from charges_map:', mapDeleteError);
      return {
        success: false,
        message: 'Failed to delete charge mappings',
        error: mapDeleteError.message
      };
    }
    
    console.log(`✅ [chargeUtils] Deleted mappings for charge ${chargeId}`);
    
    // Step 2: Delete the charge itself
    const { error: chargeDeleteError } = await supabaseClient
      .from('charges')
      .delete()
      .eq('id', chargeId);
    
    if (chargeDeleteError) {
      console.error('❌ [chargeUtils] Error deleting charge:', chargeDeleteError);
      return {
        success: false,
        message: 'Failed to delete charge',
        error: chargeDeleteError.message
      };
    }
    
    console.log(`✅ [chargeUtils] Deleted charge ${chargeId}`);
    
    // Step 3: Verify deletion
    const { data: verifyMappings, error: verifyError } = await supabaseClient
      .from('charges_map')
      .select('id')
      .eq('charge_id', chargeId);
    
    if (verifyError) {
      console.error('❌ [chargeUtils] Error verifying deletion:', verifyError);
    } else if (verifyMappings && verifyMappings.length > 0) {
      console.warn(`⚠️ [chargeUtils] Warning: ${verifyMappings.length} mappings still exist for charge ${chargeId}`);
    } else {
      console.log(`✅ [chargeUtils] Verified all mappings deleted for charge ${chargeId}`);
    }
    
    return {
      success: true,
      message: `Successfully deleted charge ${chargeId} and ${existingMappings?.length || 0} mappings`
    };
  } catch (error) {
    console.error('❌ [chargeUtils] Unexpected error in removeCharge:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}
